import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useLanguage } from '../context/LanguageContext';
import { SectionHeader } from './shared/SectionHeader';
import { API_BASE } from '../lib/api';
import axios from 'axios';

const IMAGES = {
  "ata_bag_1": "https://images.unsplash.com/photo-1667924779390-2cf1d39c1887?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxyYXR0YW4lMjBiYXNrZXQlMjB3ZWF2ZSUyMGhhbmRjcmFmdCUyMGFydGlzYW58ZW58MHx8fHwxNzc3Njk5OTAxfDA&ixlib=rb-4.1.0&q=85",
  "ata_bag_2": "https://images.unsplash.com/photo-1668008555730-c390ae0b0247?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxyYXR0YW4lMjBiYXNrZXQlMjB3ZWF2ZSUyMGhhbmRjcmFmdCUyMGFydGlzYW58ZW58MHx8fHwxNzc3Njk5OTAxfDA&ixlib=rb-4.1.0&q=85",
  "ata_bag_3": "https://images.unsplash.com/photo-1743485754062-b6ad79fd3278?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxyYXR0YW4lMjBiYXNrZXQlMjB3ZWF2ZSUyMGhhbmRjcmFmdCUyMGFydGlzYW58ZW58MHx8fHwxNzc3Njk5OTAxfDA&ixlib=rb-4.1.0&q=85",
  "lemongrass": "https://images.unsplash.com/photo-1636396279211-25e3a26b2621?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxkcmllZCUyMGxlbW9uZ3Jhc3MlMjBoZXJiJTIwc3BpY2V8ZW58MHx8fHwxNzc3Njk5OTAxfDA&ixlib=rb-4.1.0&q=85",
  "ginger": "https://images.unsplash.com/photo-1573414405995-2012861b74e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMGdpbmdlciUyMHJvb3QlMjBoYXJ2ZXN0fGVufDB8fHx8MTc3NzY5OTkwMXww&ixlib=rb-4.1.0&q=85",
  "turmeric": "https://images.unsplash.com/photo-1768729341078-9da4e0ea959e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHx0dXJtZXJpYyUyMHJvb3QlMjBwb3dkZXIlMjBzcGljZXxlbnwwfHx8fDE3Nzc2OTk5MDF8MA&ixlib=rb-4.1.0&q=85",
};

const spiceImages = ['lemongrass', 'ginger', 'turmeric'];

function BagThumbnail({ src, alt, name }) {
  return (
    <div className="relative group overflow-hidden bg-white border border-slate-100 flex-1">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full min-h-[200px] object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
        <h3 className="font-heading text-lg font-bold text-white">{name}</h3>
      </div>
    </div>
  );
}

export default function ProductCatalog() {
  const { t } = useLanguage();
  const [products, setProducts] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API_BASE}/products`, { signal: controller.signal })
      .then(res => setProducts(res.data))
      .catch(err => { if (!axios.isCancel(err)) console.error(err); });
    return () => controller.abort();
  }, []);

  const bags = products?.['ata-bags'] || [];
  const spices = products?.['spices'] || [];

  return (
    <section id="products" className="py-24 md:py-32 bg-slate-50" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader label={t('products.sectionLabel')} title={t('products.title')} />

        <Tabs defaultValue="bags" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-slate-100 p-1 h-auto">
            <TabsTrigger
              value="bags"
              className="py-3 text-sm font-bold uppercase tracking-wider data-[state=active]:bg-brand-green data-[state=active]:text-white"
              data-testid="tab-bags"
            >
              {t('products.tabBags')}
            </TabsTrigger>
            <TabsTrigger
              value="spices"
              className="py-3 text-sm font-bold uppercase tracking-wider data-[state=active]:bg-brand-green data-[state=active]:text-white"
              data-testid="tab-spices"
            >
              {t('products.tabSpices')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bags" data-testid="bags-content">
            <p className="text-slate-600 max-w-3xl mx-auto text-center mb-12 leading-relaxed">
              {t('products.bagsIntro')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-8 relative group overflow-hidden bg-white border border-slate-100">
                <img
                  src={IMAGES.ata_bag_1}
                  alt="Premium Rattan Ata Bag"
                  loading="lazy"
                  className="w-full h-[400px] md:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-8">
                  <h3 className="font-heading text-2xl font-bold text-white">{bags[0]?.name || 'Classic Round Ata Bag'}</h3>
                  <p className="text-white/70 text-sm mt-1">MOQ: {bags[0]?.moq || '100 pieces'}</p>
                </div>
              </div>

              <div className="md:col-span-4 flex flex-col gap-6">
                <BagThumbnail src={IMAGES.ata_bag_2} alt="Ata Bag Detail" name={bags[1]?.name || 'Rectangular Ata Clutch'} />
                <BagThumbnail src={IMAGES.ata_bag_3} alt="Ata Tote Basket" name={bags[2]?.name || 'Ata Tote Basket'} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {bags.map((bag, i) => (
                <div key={bag.id} className="bg-white border border-slate-100 p-8" data-testid={`bag-card-${i}`}>
                  <h4 className="font-heading text-lg font-bold text-slate-900 mb-4">{bag.name}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{bag.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500 font-medium">{t('products.material')}</span>
                      <span className="text-slate-800">{bag.details?.material}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500 font-medium">{t('products.weavingTime')}</span>
                      <span className="text-slate-800">{bag.details?.weaving_time}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500 font-medium">{t('products.finishing')}</span>
                      <span className="text-slate-800">{bag.details?.finishing}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-500 font-medium">{t('products.moq')}</span>
                      <span className="text-slate-800 font-semibold">{bag.moq}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-slate-500 font-medium">{t('products.leadTime')}</span>
                      <span className="text-slate-800">{bag.lead_time}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {bag.tags?.map((tag) => (
                      <Badge key={tag} variant="secondary" className="bg-brand-green/10 text-brand-green text-xs font-medium">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="spices" data-testid="spices-content">
            <p className="text-slate-600 max-w-3xl mx-auto text-center mb-12 leading-relaxed">
              {t('products.spicesIntro')}
            </p>

            <div className="space-y-12">
              {spices.map((spice, i) => (
                <div key={spice.id} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-testid={`spice-card-${i}`}>
                  <div className={`overflow-hidden border border-slate-100 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img
                      src={IMAGES[spiceImages[i]] || IMAGES.turmeric}
                      alt={spice.name}
                      loading="lazy"
                      className="w-full h-[300px] lg:h-[400px] object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>

                  <div className="bg-white border border-slate-100 p-8">
                    <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">{spice.name}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6">{spice.description}</p>

                    <Table>
                      <TableHeader>
                        <TableRow className="border-b-2 border-brand-green">
                          <TableHead className="font-bold text-slate-900 uppercase text-xs tracking-wider bg-slate-50">Specification</TableHead>
                          <TableHead className="font-bold text-slate-900 uppercase text-xs tracking-wider bg-slate-50">Detail</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium text-slate-600">{t('products.moistureContent')}</TableCell>
                          <TableCell className="text-slate-800">{spice.specifications?.moisture_content}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-slate-600">{t('products.dryingMethod')}</TableCell>
                          <TableCell className="text-slate-800">{spice.specifications?.drying_method}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-slate-600">{t('products.cutSize')}</TableCell>
                          <TableCell className="text-slate-800">{spice.specifications?.cut_size}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-slate-600">{t('products.shelfLife')}</TableCell>
                          <TableCell className="text-slate-800">{spice.specifications?.shelf_life}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-slate-600">{t('products.packaging')}</TableCell>
                          <TableCell className="text-slate-800">{spice.specifications?.packaging}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium text-slate-600">{t('products.origin')}</TableCell>
                          <TableCell className="text-slate-800">{spice.specifications?.origin}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{t('products.moq')}</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">{spice.moq}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{t('products.priceIndication')}</p>
                        <p className="text-lg font-bold text-brand-orange mt-1">{spice.price_indication}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4">
                      {spice.certifications?.map((cert) => (
                        <Badge key={cert} variant="outline" className="border-brand-green text-brand-green text-xs">
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
